import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Phone, Mail, MapPin, Send, CheckCircle, CalendarDays, Clock, Building2, MessageSquare, Presentation } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import axios from "axios";

// Landing backend (deploy/api/*.php) — same-origin, relative URL kullanılır.
// Prod: /api/contact, /api/demo-booking gibi.
// Dev: REACT_APP_BACKEND_URL ile override edilebilir.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const timeSlots = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30",
  "16:00", "16:30", "17:00",
];

export function ContactSection() {
  const { t } = useLanguage();
  const [mode, setMode] = useState("contact"); // "contact" or "demo"
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [demoForm, setDemoForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form, {
        headers: { "Content-Type": "application/json" },
      });
      setSent(true);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    setSending(true);
    try {
      // ISO format (YYYY-MM-DD) — backend hem bu formatı hem GG.AA.YYYY formatını kabul eder
      const yyyy = selectedDate.getFullYear();
      const mm = String(selectedDate.getMonth() + 1).padStart(2, "0");
      const dd = String(selectedDate.getDate()).padStart(2, "0");
      const dateStr = `${yyyy}-${mm}-${dd}`;
      await axios.post(`${BACKEND_URL}/api/demo-booking`, {
        ...demoForm,
        date: dateStr,
        time: selectedTime,
      }, {
        headers: { "Content-Type": "application/json" },
      });
      setSent(true);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const resetForm = () => {
    setSent(false);
    setForm({ name: "", email: "", phone: "", message: "" });
    setDemoForm({ name: "", email: "", phone: "", company: "", message: "" });
    setSelectedDate(undefined);
    setSelectedTime("");
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 sm:py-28 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-sm uppercase tracking-[0.2em] font-bold text-[#22A7D1]">
            {t("contact.overline")}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
            {t("contact.title")}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed text-slate-500">
            {t("contact.subtitle")}
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Left - Contact Info */}
          <div className="lg:col-span-2 space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 sm:p-8 overflow-hidden">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-4 sm:mb-5">{t("contact.company")}</h3>
              <div className="space-y-4">
                {[
                  { icon: Phone, label: "0224 718 54 00", sub: "Telefon", href: "tel:+902247185400", color: "bg-sky-50 text-sky-600", tid: "contact-phone" },
                  { icon: () => (
                    <svg className="w-5 h-5 text-green-600" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  ), label: "+90 533 163 16 34", sub: "WhatsApp", href: "https://wa.me/+905331631634", color: "bg-green-50", tid: "contact-whatsapp" },
                  { icon: Mail, label: "entegre@entegrepanel.net", sub: "E-posta", href: "mailto:entegre@entegrepanel.net", color: "bg-violet-50 text-violet-600", tid: "contact-email" },
                  { icon: MapPin, label: "Mesudiye Mah. Yıldırım Cad. Yokdemir İş Mrk. No:8, Bursa", sub: "Adres", color: "bg-rose-50 text-rose-600", tid: "contact-address" },
                ].map((item, i) => {
                  const Icon = item.icon;
                  const Wrapper = item.href ? "a" : "div";
                  const wrapperProps = item.href ? { href: item.href, target: item.href.startsWith("http") ? "_blank" : undefined, rel: item.href.startsWith("http") ? "noopener noreferrer" : undefined } : {};
                  return (
                    <Wrapper key={i} {...wrapperProps} className="flex items-center gap-3 text-slate-600 hover:text-slate-900 transition-colors" data-testid={item.tid}>
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${item.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="text-[11px] text-slate-400 font-medium">{item.sub}</div>
                        <div className="text-sm font-medium break-words">{item.label}</div>
                      </div>
                    </Wrapper>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-3">
            {sent ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] rounded-2xl border border-cyan-200 bg-cyan-50 p-12 text-center" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-cyan-500 mb-4" />
                <p className="text-lg font-semibold text-cyan-800">{t("contact.success")}</p>
                <button onClick={resetForm} className="mt-4 text-sm text-emerald-700 underline hover:no-underline" data-testid="contact-reset-btn">
                  {t("contact.sendAnother")}
                </button>
              </div>
            ) : (
              <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
                {/* Mode Tabs */}
                <div className="flex border-b border-slate-200">
                  <button
                    onClick={() => setMode("contact")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 sm:py-4 text-xs sm:text-sm font-semibold transition-colors ${
                      mode === "contact" ? "text-sky-700 bg-sky-50 border-b-2 border-sky-600" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid="tab-contact"
                  >
                    <MessageSquare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t("contact.tabContact")}
                  </button>
                  <button
                    onClick={() => setMode("demo")}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-3 sm:py-4 text-xs sm:text-sm font-semibold transition-colors ${
                      mode === "demo" ? "text-sky-700 bg-sky-50 border-b-2 border-sky-600" : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                    }`}
                    data-testid="tab-demo"
                  >
                    <Presentation className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    {t("contact.tabDemo")}
                  </button>
                </div>

                <div className="p-4 sm:p-6 md:p-8">
                  {mode === "contact" ? (
                    <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.name")}</label>
                        <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="contact-name-input" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.email")}</label>
                          <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="contact-email-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.phone")}</label>
                          <input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="contact-phone-input" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.message")}</label>
                        <textarea required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors resize-none" data-testid="contact-message-input" />
                      </div>
                      <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-[#22A7D1] to-[#32AED6] hover:shadow-lg hover:shadow-sky-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-60" data-testid="contact-submit-btn">
                        <Send className="w-4 h-4" />{t("contact.send")}
                      </button>
                    </form>
                  ) : (
                    <form onSubmit={handleDemoSubmit} className="space-y-4" data-testid="demo-form">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.name")}</label>
                          <input type="text" required value={demoForm.name} onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="demo-name-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.companyLabel")}</label>
                          <input type="text" value={demoForm.company} onChange={(e) => setDemoForm({ ...demoForm, company: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="demo-company-input" placeholder={t("contact.companyPlaceholder")} />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.email")}</label>
                          <input type="email" required value={demoForm.email} onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="demo-email-input" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.phone")}</label>
                          <input type="tel" value={demoForm.phone} onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors" data-testid="demo-phone-input" />
                        </div>
                      </div>

                      {/* Calendar & Time Picker */}
                      <div className="space-y-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <CalendarDays className="w-4 h-4 text-sky-600" />
                            {t("contact.selectDate")}
                          </label>
                          <div className="rounded-xl border border-slate-200 overflow-hidden bg-white flex justify-center" data-testid="demo-calendar">
                            <Calendar
                              mode="single"
                              selected={selectedDate}
                              onSelect={setSelectedDate}
                              disabled={(date) => date < new Date() || date.getDay() === 0 || date.getDay() === 6}
                              className="p-2"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
                            <Clock className="w-4 h-4 text-sky-600" />
                            {t("contact.selectTime")}
                          </label>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid="demo-time-slots">
                            {timeSlots.map((time) => (
                              <button
                                key={time}
                                type="button"
                                onClick={() => setSelectedTime(time)}
                                className={`py-2.5 text-sm font-medium rounded-lg border transition-all ${
                                  selectedTime === time
                                    ? "bg-sky-600 text-white border-sky-600 shadow-sm"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-sky-300 hover:text-sky-700"
                                }`}
                                data-testid={`time-slot-${time}`}
                              >
                                {time}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">{t("contact.demoNote")}</label>
                        <textarea rows={2} value={demoForm.message} onChange={(e) => setDemoForm({ ...demoForm, message: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-400 transition-colors resize-none" data-testid="demo-message-input" placeholder={t("contact.demoNotePlaceholder")} />
                      </div>

                      <button
                        type="submit"
                        disabled={sending || !selectedDate || !selectedTime}
                        className="w-full flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold text-white rounded-full bg-gradient-to-r from-[#22A7D1] to-[#32AED6] hover:shadow-lg hover:shadow-sky-500/20 transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="demo-submit-btn"
                      >
                        <Presentation className="w-4 h-4" />
                        {t("contact.requestDemo")}
                      </button>
                    </form>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
